/* client/src/components/Expenses.tsx */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { apiGetExpenses, apiAddExpense, apiUpdateExpense, apiDeleteExpense } from '../services/apiService';
import { apiGetBusinessUnits, apiGetExpenseCategories } from '../services/apiService';

interface Expense {
  id: number;
  date: string;
  business_unit_id: number;
  business_unit_name: string;
  category_id: number; // CHANGED: from category to category_id
  category_name: string; // ADDED: to display category name
  amount: number;
  description: string;
  created_at?: string;
}

interface BusinessUnit {
  id: number;
  name: string;
}

interface ExpenseCategory {
  id: number;
  name: string;
}

function Expenses() {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]); // ADDED: categories state
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    business_unit_id: '',
    category_id: '', // CHANGED: from category to category_id
    amount: '',
    description: ''
  });

  useEffect(() => {
    fetchExpenses();
    fetchBusinessUnits();
    fetchCategories(); // ADDED: fetch categories
  }, []);

 const fetchExpenses = async () => {
  try {
    setLoading(true);
    setError('');
    const data = await apiGetExpenses();
    setExpenses(data);
  } catch (err: any) {
    if (err.message.includes('Authentication')) {
      // Handle authentication errors by logging out
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else {
      setError(err.message || 'Failed to fetch expenses');
    }
  } finally {
    setLoading(false);
  }
};

const fetchBusinessUnits = async () => {
  try {
    const data = await apiGetBusinessUnits();
    setBusinessUnits(data);
  } catch (err: any) {
    if (err.message.includes('Authentication')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else {
      console.error('Failed to fetch business units:', err);
      setBusinessUnits([]);
    }
  }
};

const fetchCategories = async () => {
  try {
    const data = await apiGetExpenseCategories();
    setCategories(data);
  } catch (err: any) {
    if (err.message.includes('Authentication')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else {
      console.error('Failed to fetch expense categories:', err);
      setCategories([]);
    }
  }
};

  const handleOpen = (expense: Expense | null = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        date: expense.date,
        business_unit_id: expense.business_unit_id.toString(),
        category_id: expense.category_id.toString(), // CHANGED: from category to category_id
        amount: expense.amount.toString(),
        description: expense.description
      });
    } else {
      setEditingExpense(null);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        business_unit_id: '',
        category_id: '', // CHANGED: from category to category_id
        amount: '',
        description: ''
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingExpense(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      business_unit_id: '',
      category_id: '', // CHANGED: from category to category_id
      amount: '',
      description: ''
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const expenseData = {
        ...formData,
        business_unit_id: parseInt(formData.business_unit_id),
        category_id: parseInt(formData.category_id), // CHANGED: from category to category_id
        amount: parseFloat(formData.amount)
      };

      if (editingExpense) {
        await apiUpdateExpense(editingExpense.id, expenseData);
      } else {
        await apiAddExpense(expenseData);
      }
      
      fetchExpenses();
      handleClose();
    } catch (err: any) {
      setError(err.message || `Failed to ${editingExpense ? 'update' : 'add'} expense`);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await apiDeleteExpense(id);
        fetchExpenses();
      } catch (err: any) {
        setError(err.message || 'Failed to delete expense');
      }
    }
  };

  if (loading) return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
      <CircularProgress />
    </Container>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Expenses Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Expense
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Business Unit</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expenses.map((expense) => (
              <TableRow key={expense.id}>
                <TableCell>{new Date(expense.date).toLocaleDateString()}</TableCell>
                <TableCell>{expense.business_unit_name}</TableCell>
                <TableCell>{expense.category_name || 'Uncategorized'}</TableCell> {/* CHANGED: use category_name */}
                <TableCell align="right">د.{expense.amount.toFixed(2)}</TableCell>
                <TableCell>{expense.description}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(expense)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(expense.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {expenses.length === 0 && !loading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">No expenses found</Typography>
          </Box>
        )}
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingExpense ? 'Edit Expense' : 'Add New Expense'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              margin="dense"
              name="date"
              label="Date"
              type="date"
              fullWidth
              variant="outlined"
              value={formData.date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              margin="dense"
              name="business_unit_id"
              label="Business Unit"
              select
              fullWidth
              variant="outlined"
              value={formData.business_unit_id}
              onChange={handleChange}
              required
            >
              {businessUnits.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>
                  {unit.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense"
              name="category_id" // CHANGED: from category to category_id
              label="Category"
              select
              fullWidth
              variant="outlined"
              value={formData.category_id}
              onChange={handleChange}
              required
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense"
              name="amount"
              label="Amount"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.amount}
              onChange={handleChange}
              inputProps={{ step: "0.01", min: "0" }}
              required
            />
            <TextField
              margin="dense"
              name="description"
              label="Description"
              fullWidth
              variant="outlined"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingExpense ? 'Update' : 'Add'} Expense
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
}

export default Expenses;